<script setup lang="ts">
/**
 * AssistantCard — one tile in the Hub grid.
 *
 * The whole tile is a single click target into chat. It shows what a person
 * actually needs to choose: a tinted icon, the name, and one line of what
 * this assistant is for. The description used to be hidden behind a corner
 * "ⓘ" popover while the tile itself sat mostly empty — this puts the useful
 * text where the empty space was, and drops the popover (and its hover /
 * outside-click / Escape machinery) entirely. Sample questions still greet
 * you inside the chat, so nothing is lost by not carrying them here.
 *
 * The badge tint comes from `assistantTint(slug)` — see assistant-visuals.ts
 * for why each assistant gets its own hue instead of the shared brand
 * gradient.
 *
 * Accessibility: the tile *is* the anchor — one interactive element, no
 * nested controls, and a real href so middle-click and "open in new tab"
 * behave the way people expect of a link.
 */
import { computed } from "vue";
import { resolveAssistantIcon } from "../assistant-icons";
import { assistantTint } from "../assistant-visuals";

const props = withDefaults(
  defineProps<{
    /** Assistant slug — keys the tint, so the colour survives reordering. */
    slug: string;
    name: string;
    description?: string;
    /** `settings.icon` — a curated icon name; unknown/unset → monogram. */
    icon?: string;
    /** Where the card navigates (e.g. `#/chat/<slug>`). */
    href: string;
    /** Position in the grid — feeds the entrance-animation delay. */
    index?: number;
    /** A pilot (ADR 0027): tagged so people know it is still being tried. */
    pilot?: boolean;
  }>(),
  { description: "", index: 0, pilot: false },
);

const iconComp = computed(() => resolveAssistantIcon(props.icon));
const tint = computed(() => assistantTint(props.slug));

/** First letters of up to two words → a compact monogram ("Booking
 * Matcher" → "BM"). The fallback when no icon is set. */
const monogram = computed(() => {
  const words = props.name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
});

/**
 * The card shows one line, not the whole record. Operator descriptions
 * often run on into scope and disclaimer sentences ("Internal — DOIT
 * management. Recommendation only; …") that say nothing about *choosing*
 * this assistant, so we keep the opening sentence when there is a clean
 * break to cut at and let CSS clamp the rest.
 */
const tagline = computed(() => {
  const text = props.description.replace(/\s+/g, " ").trim();
  const cut = text.search(/[.!?](\s|$)/);
  if (cut >= 40 && cut <= 160) return text.slice(0, cut + 1);
  return text;
});

const style = computed(() => ({
  animationDelay: `${Math.min(props.index, 12) * 45}ms`,
  "--tint": tint.value,
}));
</script>

<template>
  <a class="acard glass rise-in" :href="href" :style="style">
    <span class="acard__badge" aria-hidden="true">
      <component
        :is="iconComp"
        v-if="iconComp"
        :size="22"
        :stroke-width="1.9"
      />
      <template v-else>{{ monogram }}</template>
    </span>
    <span class="acard__head">
      <span class="acard__name">
        {{ name }}
        <span v-if="pilot" class="acard__pilot">Pilot</span>
      </span>
      <span class="acard__arrow" aria-hidden="true">→</span>
    </span>
    <span v-if="tagline" class="acard__tagline">{{ tagline }}</span>
  </a>
</template>

<style scoped>
.acard {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
  min-height: 9.5rem;
  padding: var(--space-4) var(--space-4) var(--space-4);
  border-radius: var(--radius-lg);
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  /* Sits in the stacking order so a scaled tile rises over its neighbours
     rather than being clipped by the next one in the grid. */
  z-index: 0;
  transition:
    transform var(--dur-2) var(--ease-out),
    box-shadow var(--dur-2) var(--ease-out),
    border-color var(--dur-2) var(--ease-out),
    background var(--dur-2) var(--ease-out),
    opacity var(--dur-2) var(--ease-out);
}
/* A hairline of this assistant's own colour along the top edge — the grid
   reads as a soft spectrum rather than six identical slabs. */
.acard::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    var(--tint),
    color-mix(in oklab, var(--tint) 40%, transparent)
  );
  opacity: 0.7;
  transition: opacity var(--dur-2) var(--ease-out);
}
/* Hover styling only where hover is real. On a touchscreen `:hover` sticks
   after a tap, leaving a tile stuck in its lifted state until you tap
   elsewhere. */
@media (hover: hover) {
  /* Lift AND grow. The lift alone moved the tile 5px, which reads as a
     nudge; scaling it is what makes the tile come off the page. The shadow
     is written out rather than reusing --shadow-glow because that token is
     tuned for a 1px hint on small controls, and at tile size it disappears
     under the aurora. */
  .acard:hover {
    z-index: 2;
    transform: translateY(-6px) scale(1.025);
    background: color-mix(in srgb, var(--tint) 5%, var(--color-surface));
    border-color: color-mix(in srgb, var(--tint) 45%, var(--glass-border));
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--tint) 30%, transparent),
      0 24px 50px -18px color-mix(in srgb, var(--tint) 55%, transparent),
      0 6px 14px -8px color-mix(in srgb, var(--tint) 30%, transparent);
  }
  .acard:hover::before {
    opacity: 1;
  }
  .acard:hover .acard__badge {
    background: var(--tint);
    color: var(--color-inverse, #fff);
    transform: translateY(-1px) scale(1.04);
  }
  .acard:hover .acard__arrow {
    opacity: 1;
    background: color-mix(in srgb, var(--tint) 14%, transparent);
    transform: translateX(2px);
  }
}
/* Press. A tile that yields under the cursor is most of what makes a click
   feel like it landed; without it the lift just snaps off. */
.acard:active {
  transform: translateY(-1px) scale(1.005);
  transition-duration: var(--dur-1);
}
.acard:focus-visible .acard__arrow {
  opacity: 1;
  background: color-mix(in srgb, var(--tint) 14%, transparent);
}
.acard:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}

/* Soft tinted plate with the icon drawn in the same hue; it fills in on
   hover so the tile has one small moment of life without shouting. */
.acard__badge {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.6rem;
  height: 2.6rem;
  margin-bottom: 0.2rem;
  border-radius: 30%;
  background: color-mix(in srgb, var(--tint) 13%, transparent);
  color: var(--tint);
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition:
    background var(--dur-2) var(--ease-out),
    color var(--dur-2) var(--ease-out),
    transform var(--dur-2) var(--ease-spring);
}
.acard__name {
  font-size: 1.02rem;
  font-weight: 650;
  line-height: 1.3;
  color: var(--color-ink);
}
.acard__pilot {
  margin-left: 0.35rem;
  padding: 0.05rem 0.45rem;
  border-radius: var(--radius-pill);
  border: 1px solid color-mix(in srgb, var(--tint) 45%, transparent);
  color: var(--tint);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  vertical-align: 0.1em;
}

.acard__tagline {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--color-muted);
}

/* The title row carries the "go" cue at the end of the line the eye already
   finishes on — not the top-right corner, which reads as close/overflow. */
.acard__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  width: 100%;
}

/* Always present, just quiet. A hover-only affordance is invisible on touch,
   where there is no hover at all — the tile would offer no cue that it opens
   anything. At rest it is a faint tick of the assistant's colour; on hover it
   fills in and steps toward the direction it points. */
.acard__arrow {
  display: grid;
  place-items: center;
  flex: none;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  color: var(--tint);
  font-size: 0.9rem;
  line-height: 1;
  /* A faint plate even at rest: the circle is what makes it read as a target
     rather than as punctuation. */
  background: color-mix(in srgb, var(--tint) 7%, transparent);
  opacity: 0.55;
  transition:
    opacity var(--dur-2) var(--ease-out),
    background var(--dur-2) var(--ease-out),
    transform var(--dur-2) var(--ease-out);
}

/* Single-column stack: the fixed tile height only exists to keep a grid row
   even, so let each card shrink to its own content instead. */
@media (max-width: 560px) {
  .acard {
    min-height: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  /* Keep the colour and shadow response; drop the movement. */
  .acard:hover,
  .acard:active,
  .acard:hover .acard__badge,
  .acard:hover .acard__arrow {
    transform: none;
  }
}
</style>
